/**
 * @fileoverview The Active Clients grid's filters.
 * @description Search (bank name, Tenant ID or city), State, Owner (a sales person who owns at least
 * one client, shown as "Name (Role)") and Status (Active or Archived). Every filter is sent to the
 * backend. The State and Owner choices come from GET /sales/clients/filter-options; both selects are
 * disabled while those load or if they failed, so a half-loaded list is never mistaken for the
 * whole one. The clear button stays mounted so the row never changes height.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import Select from '@/shared/components/atoms/Select'

import TableSearchInput from './TableSearchInput'

import type { ClientFilterChange } from '@/features/sales/hooks/useActiveClientsScreen'
import type { ClientFilterOptions } from '@/features/sales/types/sales-client.types'

interface FilterValues {
  search: string
  stateId: string
  executiveUserId: string
  status: 'ACTIVE' | 'INACTIVE'
}

interface ClientsFiltersBarProps {
  values: FilterValues
  /** The select choices; undefined while they load or if they could not be loaded. */
  options: ClientFilterOptions | undefined
  onChange: (change: ClientFilterChange) => void
  onClear: () => void
  anyFilter: boolean
  /** The grid's total under these filters; undefined until the first page is in. */
  total: number | undefined
}

/**
 * @component
 * @param {ClientsFiltersBarProps} props - The filters, their choices and the handlers.
 * @param {FilterValues} props.values - The filters as set now.
 * @param {ClientFilterOptions | undefined} props.options - The State and Owner choices.
 * @param {(change: ClientFilterChange) => void} props.onChange - Applies a filter change.
 * @param {() => void} props.onClear - Resets every filter.
 * @param {boolean} props.anyFilter - Whether any filter differs from its default.
 * @param {number | undefined} props.total - How many clients match.
 */
function ClientsFiltersBar({
  values,
  options,
  onChange,
  onClear,
  anyFilter,
  total,
}: ClientsFiltersBarProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-wrap items-center gap-2.5 p-3.5 sm:p-4">
      <TableSearchInput
        value={values.search}
        onChange={(search) => onChange({ search })}
        placeholder={t('sales.activeClients.searchPlaceholder')}
        data-testid="active-clients-search-input"
      />
      <Select
        aria-label={t('sales.filterLabels.state')}
        data-testid="active-clients-state-select"
        value={values.stateId}
        disabled={!options}
        onChange={(event) => onChange({ stateId: event.target.value })}
        options={[
          { value: '', label: t('sales.activeClients.filters.allStates') },
          ...(options?.states ?? []).map((state) => ({ value: state.id, label: state.name })),
        ]}
      />
      <Select
        aria-label={t('sales.filterLabels.owner')}
        data-testid="active-clients-owner-select"
        value={values.executiveUserId}
        disabled={!options}
        onChange={(event) => onChange({ executiveUserId: event.target.value })}
        options={[
          { value: '', label: t('sales.activeClients.filters.allOwners') },
          ...(options?.executives ?? []).map((owner) => ({
            value: owner.userId,
            label: t('sales.activeClients.ownerOption', { name: owner.name, role: owner.role }),
          })),
        ]}
      />
      <Select
        aria-label={t('sales.filterLabels.status')}
        data-testid="active-clients-status-select"
        value={values.status}
        onChange={(event) =>
          onChange({ status: event.target.value === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE' })
        }
        options={[
          { value: 'ACTIVE', label: t('sales.activeClients.filters.active') },
          { value: 'INACTIVE', label: t('sales.activeClients.filters.archived') },
        ]}
      />
      <button
        type="button"
        onClick={onClear}
        data-testid="active-clients-clear-filters-button"
        disabled={!anyFilter}
        aria-hidden={!anyFilter}
        className={`rounded-lg border border-input-border bg-white px-3.5 py-2 text-compact font-semibold text-slate ${
          anyFilter ? 'cursor-pointer hover:border-border-hover hover:text-ink' : 'invisible'
        }`}
      >
        {t('sales.activeClients.filters.clear')}
      </button>
      {total !== undefined && (
        <span className="text-xs text-slate" data-testid="active-clients-count">
          {t('sales.activeClients.countLine', { count: total })}
        </span>
      )}
    </div>
  )
}

export default ClientsFiltersBar
